import { Alert, Button, PageHeader, Spinner } from '@cartly/ui';
import { Printer } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { api } from '../../api/client';
import { formatDate, formatMoney } from '../../app/format';
import { useLoader } from '../../app/hooks';

interface Receipt {
  receiptNumber: string;
  organization: string;
  plan: string;
  days: number;
  amountMinor: number;
  currency: string;
  paidAt: string;
  reference: string;
  method: string;
  invoice: Invoice | null;
}

interface Invoice {
  number: string;
  seller: { legalName: string; gstin: string; address: string; stateCode: string };
  buyer: { legalName: string; gstin: string | null; address: string | null; stateCode: string };
  sac: string;
  ratePercent: number;
  description: string;
  taxableMinor: number;
  cgstMinor: number;
  sgstMinor: number;
  igstMinor: number;
  totalMinor: number;
  placeOfSupply: string;
  issuedAt?: string;
}

/** Printable payment receipt. Use the browser's Print > Save as PDF. */
export function ReceiptPage() {
  const { id } = useParams();
  const receipt = useLoader(() => api.get<Receipt>(`/billing/payments/${id}/receipt`), id);
  if (receipt.loading && !receipt.data) return <Spinner size="lg" label="Loading" />;
  if (receipt.error || !receipt.data) {
    return <Alert tone="critical">{receipt.error ?? 'Receipt not found'}</Alert>;
  }
  const r = receipt.data;
  if (r.invoice) return <TaxInvoice r={r} invoice={r.invoice} />;
  return (
    <div className="stack-lg receipt">
      <PageHeader
        title={`Receipt ${r.receiptNumber}`}
        description="Payment receipt for your Cartly360 plan. This is not a GST tax invoice."
        actions={
          <div className="row">
            <Link to="/billing" className="btn btn--secondary">
              Back
            </Link>
            <Button icon={<Printer size={16} />} onClick={() => window.print()}>
              Print or save as PDF
            </Button>
          </div>
        }
      />
      <div className="card">
        <div className="card__body">
          <dl className="receipt__rows">
            <dt>Organization</dt>
            <dd>{r.organization}</dd>
            <dt>Plan</dt>
            <dd>
              {r.plan}, {r.days} days
            </dd>
            <dt>Amount paid</dt>
            <dd>{formatMoney(String(r.amountMinor / 100), r.currency)}</dd>
            <dt>Paid on</dt>
            <dd>{formatDate(r.paidAt)}</dd>
            <dt>Method</dt>
            <dd>{r.method}</dd>
            <dt>Reference</dt>
            <dd>{r.reference}</dd>
          </dl>
        </div>
      </div>
    </div>
  );
}

const inr = (minor: number, currency: string) => formatMoney(String(minor / 100), currency);

/** GST tax invoice: seller and buyer, SAC, taxable value and the CGST/SGST or IGST split. */
function TaxInvoice({ r, invoice: i }: { r: Receipt; invoice: Invoice }) {
  return (
    <div className="stack-lg receipt">
      <PageHeader
        title={`Tax invoice ${i.number}`}
        description={`Issued ${formatDate(i.issuedAt ?? r.paidAt)}`}
        actions={
          <div className="row">
            <Link to="/billing" className="btn btn--secondary">
              Back
            </Link>
            <Button icon={<Printer size={16} />} onClick={() => window.print()}>
              Print or save as PDF
            </Button>
          </div>
        }
      />
      <div className="card">
        <div className="card__body stack">
          <div className="rule-grid">
            <div>
              <strong>Sold by</strong>
              <div>{i.seller.legalName}</div>
              <div className="text-muted">{i.seller.address}</div>
              <div>GSTIN {i.seller.gstin}</div>
            </div>
            <div>
              <strong>Billed to</strong>
              <div>{i.buyer.legalName}</div>
              {i.buyer.address && <div className="text-muted">{i.buyer.address}</div>}
              {i.buyer.gstin && <div>GSTIN {i.buyer.gstin}</div>}
              <div className="text-muted">Place of supply: state code {i.placeOfSupply}</div>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>SAC</th>
                  <th>Taxable value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{i.description}</td>
                  <td>{i.sac}</td>
                  <td>{inr(i.taxableMinor, r.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <dl className="receipt__rows">
            {i.cgstMinor > 0 && (
              <>
                <dt>CGST ({i.ratePercent / 2}%)</dt>
                <dd>{inr(i.cgstMinor, r.currency)}</dd>
                <dt>SGST ({i.ratePercent / 2}%)</dt>
                <dd>{inr(i.sgstMinor, r.currency)}</dd>
              </>
            )}
            {i.igstMinor > 0 && (
              <>
                <dt>IGST ({i.ratePercent}%)</dt>
                <dd>{inr(i.igstMinor, r.currency)}</dd>
              </>
            )}
            <dt>
              <strong>Total</strong>
            </dt>
            <dd>
              <strong>{inr(i.totalMinor, r.currency)}</strong>
            </dd>
            <dt>Paid on</dt>
            <dd>
              {formatDate(r.paidAt)} · {r.method}
            </dd>
            <dt>Reference</dt>
            <dd>{r.reference}</dd>
          </dl>
        </div>
      </div>
    </div>
  );
}
